import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

interface ExternalSourceErrorBadgeProps {
  errors: { key: string; label: string }[];
}

const ExternalSourceErrorBadge: React.FC<ExternalSourceErrorBadgeProps> = ({ errors }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (errors.length === 0) return null;

  if (expanded) {
    return (
      <div className="absolute bottom-36 sm:bottom-24 left-3 z-[1000] pointer-events-auto animate-in fade-in duration-200">
        <div className="bg-[var(--bg-surface)] border border-amber-500/30 rounded-2xl shadow-2xl p-3 max-w-[220px]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <AlertTriangle size={13} className="text-amber-500" />
              <span className="text-2xs font-black text-amber-600">{t('layer_error.badge_title')}</span>
            </div>
            <button onClick={() => setExpanded(false)} className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X size={12} />
            </button>
          </div>
          <p className="text-3xs text-[var(--text-secondary)] leading-relaxed mb-2">
            {t('layer_error.source_unavailable')}
          </p>
          <div className="space-y-1">
            {errors.map(e => (
              <div key={e.key} className="text-3xs font-bold text-[var(--text-primary)] bg-amber-500/10 rounded-lg px-2 py-1">
                {e.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute bottom-36 sm:bottom-24 left-3 z-[1000] pointer-events-auto">
      <button
        onClick={() => setExpanded(true)}
        className="relative w-8 h-8 bg-amber-500 rounded-xl flex items-center justify-center shadow-xl active:scale-95 transition-all animate-in fade-in duration-300"
        title={t('layer_error.badge_title')}
      >
        <AlertTriangle size={15} className="text-white" />
        {errors.length > 1 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-4xs font-black flex items-center justify-center">
            {errors.length}
          </span>
        )}
      </button>
    </div>
  );
};

export default ExternalSourceErrorBadge;
